import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  Suspense,
  lazy,
} from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import useWebSocket from "react-use-websocket";
import "./App.css";
import "./enhanced-styles.css";
import "./enhanced-ui-improvements.css"; // Import enhanced styles
import ImpersonationBanner from "./admin/ImpersonationBanner";
// ---------------------------------------------------------------------------
// Eager imports: kept on the initial bundle because they render on every
// navigation (layout chrome, guards, toast host) or because they are tiny
// utilities/data objects that would not benefit from a separate chunk.
// ---------------------------------------------------------------------------
import { ProtectedRoute, GuestRoute } from "./auth-guards";
import { Header } from "./components";
import { EnhancedFooter } from "./enhanced-landing-pages.js";
import { ToastProvider, useToast } from "./toast";
import ScrollToTop from "./components/ScrollTopBtn.jsx";
import { AdminRoute } from "./AdminRoute";
import { RouteSEO } from "./components/RouteSEO";
import { PageLoader } from "./components/LoadingSpinner";
import { getSocketUrl } from "./lib/utils.js";
import { getToken, clearAuth } from "./lib/storage";
// Cross-cutting platform primitives — trust, presence, notifications,
// command palette, locale/currency, and a11y. See src/components/* for
// per-module docs.
import {
  PresenceProvider,
  usePresence,
} from "./components/presence/PresenceContext";
import {
  NotificationProvider,
  useNotifications,
} from "./components/notifications/NotificationCenter";
import { CommandPaletteProvider } from "./components/command/CommandPalette";
import { LocaleProvider } from "./components/i18n/LocaleContext";
import { SkipToContent } from "./components/a11y/SkipToContent";
import {
  MessagesPanelProvider,
  useMessagesPanel,
} from "./components/messages/MessagesPanelContext";
import MessagesPanel from "./components/messages/MessagesPanel";
import MaintenancePage from "./MaintenancePage";
import {
  webDevelopmentData,
  mobileAppsData,
  aiDevelopmentData,
  marketingAutomationData,
  technicalConsultingData,
} from "./enhanced-services-pages";
import {
  contentCreationData,
  designServicesData,
  digitalMarketingData,
  businessConsultingData,
  dataAnalyticsData,
} from "./additional-services-data";

// ---------------------------------------------------------------------------
// Lazy-loaded pages. Each import() becomes its own JS chunk, so the initial
// marketing home page no longer has to download admin/contracts/video/etc.
// `lazyNamed` wraps named exports (React.lazy itself only handles default
// exports). `lazyDefault` is a tiny alias kept for readability.
// ---------------------------------------------------------------------------
const lazyNamed = (importFn, name) =>
  lazy(() => importFn().then((m) => ({ default: m[name] })));
const lazyDefault = (importFn) => lazy(importFn);

// Blog CMS imports
import { BlogAuthProvider, useBlogAuth } from "./blog/BlogAuthContext";
const BlogLandingPage = lazyDefault(() => import("./blog/BlogLandingPage"));
const BlogPostPage = lazyDefault(() => import("./blog/BlogPostPage"));
const CMSLoginPage = lazyDefault(() => import("./blog/CMSLoginPage"));
const CMSLayout = lazyDefault(() => import("./blog/CMSLayout"));
const CMSDashboard = lazyDefault(() => import("./blog/CMSDashboard"));
const CMSPostsList = lazyDefault(() => import("./blog/CMSPostsList"));
const CMSPostEditor = lazyDefault(() => import("./blog/CMSPostEditor"));
const CMSTagsPage = lazyDefault(() => import("./blog/CMSTagsPage"));
const CMSAuthorsPage = lazyDefault(() => import("./blog/CMSAuthorsPage"));
const CMSSettingsPage = lazyDefault(() => import("./blog/CMSSettingsPage"));
const CMSSubscribersPage = lazyDefault(
  () => import("./blog/CMSSubscribersPage"),
);

// CMS Protected Route
function CMSProtectedRoute({ children }) {
  const { cmsUser, cmsLoading } = useBlogAuth();
  if (cmsLoading) return <PageLoader />;
  if (!cmsUser) return <CMSLoginPage />;
  return children;
}

// Home & core browse
const HomePage = lazyNamed(() => import("./components"), "HomePage");
const EnhancedJobsPage = lazyNamed(
  () => import("./enhanced-jobs-page"),
  "EnhancedJobsPage",
);
const JobDetailsPage = lazyNamed(() => import("./job-pages"), "JobDetailsPage");
const FreelancerProfilePage = lazyNamed(
  () => import("./freelancer-profile"),
  "FreelancerProfilePage",
);
const StaticInfoPage = lazyNamed(
  () => import("./static-pages"),
  "StaticInfoPage",
);

// Services
const EnhancedServicePage = lazyNamed(
  () => import("./enhanced-services-pages"),
  "EnhancedServicePage",
);
const AIServicesPage = lazyNamed(
  () => import("./ai-services-page"),
  "AIServicesPage",
);
const WebDevelopmentPage = lazyNamed(
  () => import("./unified-category-pages"),
  "WebDevelopmentPage",
);
const MobileAppPage = lazyNamed(
  () => import("./unified-category-pages"),
  "MobileAppPage",
);
const DesignServicesPage = lazyNamed(
  () => import("./unified-category-pages"),
  "DesignServicesPage",
);
const DigitalMarketingPage = lazyNamed(
  () => import("./unified-category-pages"),
  "DigitalMarketingPage",
);
const ContentCreationPage = lazyNamed(
  () => import("./unified-category-pages"),
  "ContentCreationPage",
);
const DataAnalyticsPage = lazyNamed(
  () => import("./unified-category-pages"),
  "DataAnalyticsPage",
);
const BusinessConsultingPage = lazyNamed(
  () => import("./unified-category-pages"),
  "BusinessConsultingPage",
);
const ServicesOverviewPage = lazyNamed(
  () => import("./services-overview"),
  "ServicesOverviewPage",
);

// Marketing / comprehensive / legal
const AboutPage = lazyNamed(() => import("./comprehensive-pages"), "AboutPage");
const HowItWorksPage = lazyNamed(
  () => import("./comprehensive-pages"),
  "HowItWorksPage",
);
const PricingPage = lazyNamed(
  () => import("./comprehensive-pages-2"),
  "PricingPage",
);
const TrustSafetyPage = lazyNamed(
  () => import("./comprehensive-pages-2"),
  "TrustSafetyPage",
);
const CareersPage = lazyNamed(
  () => import("./comprehensive-pages-3"),
  "CareersPage",
);
const TermsOfServicePage = lazyNamed(
  () => import("./legal-pages"),
  "TermsOfServicePage",
);
const PrivacyPolicyPage = lazyNamed(
  () => import("./legal-pages"),
  "PrivacyPolicyPage",
);
const NotFoundPage = lazyNamed(
  () => import("./legal-pages-404"),
  "NotFoundPage",
);
const ContactUsPage = lazyNamed(
  () => import("./legal-pages-contact"),
  "ContactUsPage",
);
const CareerBuddyProProPage = lazyNamed(
  () => import("./worksoypro-pages"),
  "CareerBuddyProProPage",
);
const ManageProjectsPage = lazyNamed(
  () => import("./project-management-pages"),
  "ManageProjectsPage",
);
const GetVerifiedExpertPage = lazyNamed(
  () => import("./verification-pages"),
  "GetVerifiedExpertPage",
);

// Auth & security
const RegistrationPage = lazyNamed(
  () => import("./auth-components"),
  "RegistrationPage",
);
const EnhancedLoginPage = lazyNamed(
  () => import("./auth-components"),
  "LoginPage",
);
const EmailVerificationPage = lazyNamed(
  () => import("./auth-components"),
  "EmailVerificationPage",
);
const ForgotPasswordPage = lazyNamed(
  () => import("./auth-components"),
  "ForgotPasswordPage",
);
const ResetPasswordPage = lazyNamed(
  () => import("./auth-components"),
  "ResetPasswordPage",
);
const SecuritySettingsPage = lazyNamed(
  () => import("./security-settings"),
  "SecuritySettingsPage",
);

// AI / team / subscriptions / video / gigs
const AIJobMatching = lazyNamed(() => import("./ai-features"), "AIJobMatching");
const AIProposalAssistant = lazyNamed(
  () => import("./ai-features"),
  "AIProposalAssistant",
);
const AIFeaturesPage = lazyNamed(
  () => import("./ai-features-page"),
  "AIFeaturesPage",
);
const EnhancedTeamWorkspacesPage = lazyNamed(
  () => import("./team-workspaces-enhanced"),
  "EnhancedTeamWorkspacesPage",
);
const SubscriptionsPage = lazyNamed(
  () => import("./subscriptions"),
  "SubscriptionsPage",
);
const EnhancedSubscriptionsPage = lazyNamed(
  () => import("./subscriptions-enhanced"),
  "EnhancedSubscriptionsPage",
);
const EnhancedVideoInterviewsPage = lazyNamed(
  () => import("./video-interviews-enhanced"),
  "EnhancedVideoInterviewsPage",
);
const EnhancedVideoRoomPage = lazyNamed(
  () => import("./video-interviews-enhanced"),
  "EnhancedVideoRoomPage",
);
const CreateGigPage = lazyNamed(
  () => import("./skills-marketplace"),
  "CreateGigPage",
);
const EnhancedSkillsMarketplacePage = lazyNamed(
  () => import("./skills-marketplace-enhanced"),
  "EnhancedSkillsMarketplacePage",
);
const EnhancedGigDetailsPage = lazyNamed(
  () => import("./skills-marketplace-enhanced"),
  "EnhancedGigDetailsPage",
);
const UpdatedEnterprisePage = lazyNamed(
  () => import("./updated-enterprise"),
  "UpdatedEnterprisePage",
);
const HowItWorksFreelancersPage = lazyNamed(
  () => import("./updated-how-it-works-freelancers"),
  "HowItWorksFreelancersPage",
);

// Marketing / resources / search / messages
const PressPage = lazyNamed(() => import("./marketing-pages"), "PressPage");
const HelpCenterPage = lazyNamed(
  () => import("./marketing-pages"),
  "HelpCenterPage",
);
const PartnersPage = lazyNamed(
  () => import("./marketing-pages"),
  "PartnersPage",
);
const CalculatorPage = lazyNamed(
  () => import("./marketing-pages"),
  "CalculatorPage",
);
const HiringGuidePage = lazyNamed(
  () => import("./marketing-pages"),
  "HiringGuidePage",
);
const FreelancerGuidePage = lazyNamed(
  () => import("./marketing-pages"),
  "FreelancerGuidePage",
);
const DesignSystemDemo = lazyNamed(
  () => import("./design-system-demo"),
  "DesignSystemDemo",
);
const MessagesPage = lazyNamed(() => import("./messages-page"), "MessagesPage");
const AdvancedSearchInterface = lazyNamed(
  () => import("./advanced-search-ui"),
  "AdvancedSearchInterface",
);
const SavedSearchesManager = lazyNamed(
  () => import("./advanced-search-ui"),
  "SavedSearchesManager",
);

// Companies / freelancer workflow
const ForCompaniesPage = lazyNamed(
  () => import("./company-pages"),
  "ForCompaniesPage",
);
const SignContractsPage = lazyNamed(
  () => import("./company-pages"),
  "SignContractsPage",
);
const ManageTeamProjectsPage = lazyNamed(
  () => import("./company-pages"),
  "ManageTeamProjectsPage",
);
const ShareHiringWorkspacePage = lazyNamed(
  () => import("./company-pages"),
  "ShareHiringWorkspacePage",
);
const ApproveInvoicesPage = lazyNamed(
  () => import("./company-pages"),
  "ApproveInvoicesPage",
);
const ForFreelancersPage = lazyNamed(
  () => import("./company-pages"),
  "ForFreelancersPage",
);
const JoinAsTalentPage = lazyDefault(() => import("./cbpro/JoinAsTalentPage"));
const ManagedEmbeddedPage = lazyDefault(
  () => import("./cbpro/ManagedEmbeddedPage"),
);
const ForAgenciesPage = lazyNamed(
  () => import("./company-pages"),
  "ForAgenciesPage",
);
const ForStartupsPage = lazyNamed(
  () => import("./company-pages"),
  "ForStartupsPage",
);
const EscrowPage = lazyNamed(() => import("./escrow-pages"), "EscrowPage");
const FreelancerSettingsPage = lazyNamed(
  () => import("./freelancer-settings"),
  "FreelancerSettingsPage",
);
const NegotiationPage = lazyNamed(
  () => import("./negotiation-page"),
  "NegotiationPage",
);
const DeliverablesPage = lazyNamed(
  () => import("./deliverables-page"),
  "DeliverablesPage",
);
const ContractsPage = lazyNamed(
  () => import("./contracts-page"),
  "ContractsPage",
);
const PaymentConfirmPage = lazyNamed(
  () => import("./payment-confirm-page"),
  "PaymentConfirmPage",
);
const ProposalsPage = lazyNamed(
  () => import("./proposals-page"),
  "ProposalsPage",
);
const MyJobsPage = lazyNamed(() => import("./my-jobs-page"), "MyJobsPage");
const TaskBoardPage = lazyNamed(
  () => import("./taskboard-page"),
  "TaskBoardPage",
);
const RecommendationsPage = lazyNamed(
  () => import("./recommendations-page"),
  "RecommendationsPage",
);
const AlertsPage = lazyNamed(() => import("./alerts-page"), "AlertsPage");
const VettingPage = lazyNamed(() => import("./vetting-page"), "VettingPage");
const AgenciesPage = lazyNamed(() => import("./agencies-page"), "AgenciesPage");

// Admin & wallet / payouts
const AdminDashboard = lazyNamed(
  () => import("./admin-dashboard"),
  "AdminDashboard",
);
const AdminOpsShell = lazyNamed(
  () => import("./admin-ops-shell"),
  "AdminOpsShell",
);
const NotificationPreferencesPage = lazyNamed(
  () => import("./notification-preferences-page"),
  "NotificationPreferencesPage",
);
const WalletPage = lazyNamed(() => import("./wallet-page"), "WalletPage");
const FeeManagementPage = lazyDefault(() => import("./fee-management-page"));
const FileUploadDemoPage = lazyNamed(
  () => import("./file-upload-demo"),
  "FileUploadDemoPage",
);

// Onboarding & discovery
const FreelancerOnboardingPage = lazyNamed(
  () => import("./freelancer-onboarding"),
  "FreelancerOnboardingPage",
);
const OnboardingSuccessPage = lazyNamed(
  () => import("./freelancer-onboarding"),
  "OnboardingSuccessPage",
);
const FindFreelancersPage = lazyNamed(
  () => import("./find-freelancers"),
  "FindFreelancersPage",
);
const FreelancerDetailPage = lazyNamed(
  () => import("./find-freelancers"),
  "FreelancerProfilePage",
);

// Resources
const PartnerNetworksPage = lazyNamed(
  () => import("./resource-pages"),
  "PartnerNetworksPage",
);
const ProjectCalculatorPage = lazyNamed(
  () => import("./resource-pages"),
  "ProjectCalculatorPage",
);
const RemoteHiringGuidePage = lazyNamed(
  () => import("./resource-pages"),
  "RemoteHiringGuidePage",
);
const FreelancerSuccessGuidePage = lazyNamed(
  () => import("./resource-pages"),
  "FreelancerSuccessGuidePage",
);

// Project management / unified job flows / hiring
const ProjectManagementDashboard = lazyNamed(
  () => import("./project-management-dashboard.js"),
  "ProjectManagementDashboard",
);
const UnifiedPostJobPage = lazyDefault(() => import("./post-job-unified.js"));
const UnifiedFindTalentPage = lazyDefault(
  () => import("./find-talent-unified"),
);
const HiringConfirmationPage = lazyDefault(
  () => import("./HiringConfirmationPage"),
);

// Memoized search page components to prevent re-renders
const SearchJobsPageWrapper = React.memo(({ isLoggedIn }) => {
  const handleSearchResults = React.useCallback((r) => {
    console.log("jobs search resp", r);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <AdvancedSearchInterface
          searchType="jobs"
          isLoggedIn={isLoggedIn}
          onSearchResults={handleSearchResults}
        />
      </div>
    </div>
  );
});

const SearchFreelancersPageWrapper = React.memo(({ isLoggedIn }) => {
  const handleSearchResults = React.useCallback((r) => {
    console.log("freelancers search resp", r);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <AdvancedSearchInterface
          searchType="freelancers"
          isLoggedIn={isLoggedIn}
          onSearchResults={handleSearchResults}
        />
      </div>
    </div>
  );
});

const SavedSearchesPageWrapper = React.memo(({ isLoggedIn }) => (
  <div className="min-h-screen bg-gray-50">
    <div className="max-w-7xl mx-auto px-4 py-8">
      <SavedSearchesManager isLoggedIn={isLoggedIn} />
    </div>
  </div>
));

// Initialize auth state synchronously from storage to prevent redirects on refresh
const getInitialAuthState = () => {
  try {
    const storedToken = getToken();
    if (storedToken) {
      return {
        isLoggedIn: true,
        userType: localStorage.getItem("userType"),
        user: JSON.parse(localStorage.getItem("user")),
      };
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("Error reading auth state:", e);
  }
  return {
    isLoggedIn: false,
    userType: "",
    user: null,
  };
};

const Root = ({ isLoggedIn, userType, user, onLogout }) => {
  const location = useLocation();
  const [adminDarkMode, setAdminDarkMode] = useState(
    () => localStorage.getItem("adminDarkMode") === "true",
  );

  useEffect(() => {
    if (location.pathname.startsWith("/admin")) {
      localStorage.setItem("adminDarkMode", adminDarkMode);
      if (adminDarkMode) document.documentElement.classList.add("dark");
      else document.documentElement.classList.remove("dark");
    }
  }, [adminDarkMode, location.pathname]);

  useEffect(() => {
    // Your logic to handle the route change goes here
    console.log("Route changed to:", location.pathname);
    window.scrollTo(0, 0);
    // e.g., send a page view event to analytics
  }, [location.pathname]);

  return (
    <>
      <ImpersonationBanner />
      {location.pathname.startsWith("/cms") ? (
        <BlogAuthProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/cms/login" element={<CMSLoginPage />} />
              <Route
                path="/cms"
                element={
                  <CMSProtectedRoute>
                    <CMSLayout />
                  </CMSProtectedRoute>
                }
              >
                <Route index element={<CMSDashboard />} />
                <Route path="posts" element={<CMSPostsList />} />
                <Route path="posts/new" element={<CMSPostEditor />} />
                <Route path="posts/:postId/edit" element={<CMSPostEditor />} />
                <Route path="tags" element={<CMSTagsPage />} />
                <Route path="authors" element={<CMSAuthorsPage />} />
                <Route path="subscribers" element={<CMSSubscribersPage />} />
                <Route path="settings" element={<CMSSettingsPage />} />
              </Route>
            </Routes>
          </Suspense>
        </BlogAuthProvider>
      ) : !userType ||
        (userType &&
          ["admin", "freelancer", "client"].includes(userType) &&
          !location.pathname.startsWith("/admin")) ? (
        <>
          <RouteSEO />
          <Header
            isLoggedIn={isLoggedIn}
            userType={userType}
            user={user}
            onLogout={onLogout}
          />
          <main id="main-content">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                {/* Enhanced Pages */}
                <Route path="/jobs" element={<EnhancedJobsPage />} />
                {/* <Route path="/talent" element={<EnhancedTalentPage />} /> */}
                <Route
                  path="/talent"
                  element={<UnifiedFindTalentPage isLoggedIn={isLoggedIn} />}
                />
                <Route
                  path="/talent/top"
                  element={<UnifiedFindTalentPage isLoggedIn={isLoggedIn} />}
                />
                <Route
                  path="/talent/music-audio"
                  element={<UnifiedFindTalentPage isLoggedIn={isLoggedIn} />}
                />
                <Route
                  path="/talent/social-media"
                  element={<UnifiedFindTalentPage isLoggedIn={isLoggedIn} />}
                />
                <Route
                  path="/talent/video-animation"
                  element={<UnifiedFindTalentPage isLoggedIn={isLoggedIn} />}
                />
                <Route
                  path="/talent/product"
                  element={<UnifiedFindTalentPage isLoggedIn={isLoggedIn} />}
                />
                <Route
                  path="/talent/other"
                  element={<UnifiedFindTalentPage isLoggedIn={isLoggedIn} />}
                />

                <Route path="/jobs/:jobId" element={<JobDetailsPage />} />
                <Route
                  path="/freelancers/:id"
                  element={<FreelancerProfilePage />}
                />

                {/* Discover/Search - Full Advanced UI */}
                <Route
                  path="/search/jobs"
                  element={<SearchJobsPageWrapper isLoggedIn={isLoggedIn} />}
                />
                <Route
                  path="/search/freelancers"
                  element={
                    <SearchFreelancersPageWrapper isLoggedIn={isLoggedIn} />
                  }
                />
                <Route
                  path="/saved-searches"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <SavedSearchesPageWrapper isLoggedIn={isLoggedIn} />
                    </ProtectedRoute>
                  }
                />

                {/* Messages - Protected */}
                <Route
                  path="/messages"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <MessagesPage />
                    </ProtectedRoute>
                  }
                />

                {/* Enhanced Authentication - Guest Only */}
                <Route
                  path="/login"
                  element={
                    <GuestRoute isLoggedIn={isLoggedIn}>
                      <EnhancedLoginPage />
                    </GuestRoute>
                  }
                />
                <Route
                  path="/register"
                  element={
                    <GuestRoute isLoggedIn={isLoggedIn}>
                      <RegistrationPage />
                    </GuestRoute>
                  }
                />
                <Route
                  path="/signup"
                  element={
                    <GuestRoute isLoggedIn={isLoggedIn}>
                      <RegistrationPage />
                    </GuestRoute>
                  }
                />
                <Route
                  path="/verify-email"
                  element={
                    <GuestRoute isLoggedIn={isLoggedIn}>
                      <EmailVerificationPage />
                    </GuestRoute>
                  }
                />
                <Route
                  path="/forgot-password"
                  element={
                    <GuestRoute isLoggedIn={isLoggedIn}>
                      <ForgotPasswordPage />
                    </GuestRoute>
                  }
                />
                <Route
                  path="/reset-password"
                  element={
                    <GuestRoute isLoggedIn={isLoggedIn}>
                      <ResetPasswordPage />
                    </GuestRoute>
                  }
                />
                <Route
                  path="/security"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <SecuritySettingsPage />
                    </ProtectedRoute>
                  }
                />

                {/* Post Job - Protected */}
                <Route
                  path="/post-job"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <UnifiedPostJobPage />
                    </ProtectedRoute>
                  }
                />

                {/* Escrow - Protected */}
                <Route
                  path="/escrow"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <EscrowPage />
                    </ProtectedRoute>
                  }
                />

                {/* Freelancer Settings - Protected */}
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <FreelancerSettingsPage />
                    </ProtectedRoute>
                  }
                />

                {/* Project & Workflow Pages - Protected */}
                <Route
                  path="/negotiation"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <NegotiationPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/deliverables"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <DeliverablesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/contracts"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <ContractsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/contracts/:contractId"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <ContractsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payment-confirm"
                  element={<PaymentConfirmPage />}
                />
                <Route
                  path="/proposals"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <ProposalsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/proposals/:proposalId"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <ProposalsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hiring/confirm/:proposalId"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <HiringConfirmationPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/my-jobs"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <MyJobsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/taskboard"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <TaskBoardPage />
                    </ProtectedRoute>
                  }
                />

                {/* Project dashboard - Protected */}
                <Route
                  path="/manage-project"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <ProjectManagementDashboard
                        isLoggedIn={isLoggedIn}
                        user={user}
                        userType={userType}
                      />
                    </ProtectedRoute>
                  }
                />

                {/* Talent & Discovery Pages - Protected */}
                <Route
                  path="/recommendations"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <RecommendationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/alerts"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <AlertsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vetting"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <VettingPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agencies"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <AgenciesPage />
                    </ProtectedRoute>
                  }
                />

                {/* Notification Preferences - Protected */}
                <Route
                  path="/notifications"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <NotificationPreferencesPage />
                    </ProtectedRoute>
                  }
                />

                {/* Wallet - Protected */}
                <Route
                  path="/wallet"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <WalletPage />
                    </ProtectedRoute>
                  }
                />

                {/* File Upload Demo */}
                <Route path="/upload-demo" element={<FileUploadDemoPage />} />

                {/* Design System Demo */}
                <Route path="/design-system" element={<DesignSystemDemo />} />

                {/* Marketing & Footer Pages */}
                <Route path="/press" element={<PressPage />} />
                <Route path="/help" element={<HelpCenterPage />} />
                {/* Blog Routes - Public */}
                <Route path="/blog" element={<BlogLandingPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/partners" element={<PartnersPage />} />
                <Route path="/calculator" element={<CalculatorPage />} />
                <Route path="/hiring-guide" element={<HiringGuidePage />} />
                <Route
                  path="/freelancer-guide"
                  element={<FreelancerGuidePage />}
                />

                {/* Enhanced Resource Pages */}
                <Route
                  path="/partner-networks"
                  element={<PartnerNetworksPage />}
                />
                {/* <Route path="/blog-guides" element={<BlogGuidesPage />} /> */}
                <Route
                  path="/project-calculator"
                  element={<ProjectCalculatorPage />}
                />
                <Route
                  path="/remote-hiring-guide"
                  element={<RemoteHiringGuidePage />}
                />
                <Route
                  path="/freelancer-success-guide"
                  element={<FreelancerSuccessGuidePage />}
                />

                {/* Freelancer Onboarding & Search - Protected */}
                <Route
                  path="/freelancer-onboarding"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <FreelancerOnboardingPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/onboarding/success"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <OnboardingSuccessPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/find-freelancers"
                  element={<FindFreelancersPage />}
                />
                <Route
                  path="/find-talent"
                  element={<UnifiedFindTalentPage isLoggedIn={isLoggedIn} />}
                />
                <Route
                  path="/freelancer/:id"
                  element={<FreelancerDetailPage />}
                />

                {/* New Footer Pages */}
                <Route path="/how-it-works" element={<HowItWorksPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/trust-safety" element={<TrustSafetyPage />} />

                {/* Service Pages */}
                {/* Services Overview */}
                <Route path="/services" element={<ServicesOverviewPage />} />

                <Route
                  path="/careerbuddypropro"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <CareerBuddyProProPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/freelancer-dashboard"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <CareerBuddyProProPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/manage"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <ManageProjectsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/verification"
                  element={<GetVerifiedExpertPage />}
                />
                <Route path="/experts" element={<GetVerifiedExpertPage />} />

                {/* Enhanced Service Pages */}
                <Route
                  path="/services/web-development"
                  element={<WebDevelopmentPage />}
                />
                <Route
                  path="/services/mobile-apps"
                  element={<MobileAppPage />}
                />
                <Route
                  path="/services/ai-development"
                  element={<AIServicesPage />}
                />
                <Route path="/services/ai" element={<AIServicesPage />} />
                <Route
                  path="/services/marketing-automation"
                  element={<EnhancedServicePage {...marketingAutomationData} />}
                />
                <Route
                  path="/services/technical-consulting"
                  element={<EnhancedServicePage {...technicalConsultingData} />}
                />

                {/* Additional Enhanced Service Pages */}
                <Route
                  path="/services/content-creation"
                  element={<ContentCreationPage />}
                />
                <Route
                  path="/services/writing"
                  element={<ContentCreationPage />}
                />
                <Route
                  path="/services/design"
                  element={<DesignServicesPage />}
                />
                <Route
                  path="/services/digital-marketing"
                  element={<DigitalMarketingPage />}
                />
                <Route
                  path="/services/marketing"
                  element={<DigitalMarketingPage />}
                />
                <Route
                  path="/services/business-consulting"
                  element={<BusinessConsultingPage />}
                />
                <Route
                  path="/services/consulting"
                  element={<BusinessConsultingPage />}
                />
                <Route
                  path="/services/data-analytics"
                  element={<DataAnalyticsPage />}
                />
                <Route
                  path="/services/virtual-assistant"
                  element={<EnhancedServicePage {...businessConsultingData} />}
                />

                {/* Static Info Pages (Footer links) */}
                <Route
                  path="/how-to-hire"
                  element={
                    <StaticInfoPage
                      title="How to Hire"
                      description="Best practices to hire top freelancers on CareerBuddyPro."
                      sections={[
                        {
                          heading: "Define the role",
                          content: "Write a clear scope, timeline, and budget.",
                        },
                        {
                          heading: "Evaluate proposals",
                          content:
                            "Check portfolios, reviews, and communication.",
                        },
                        {
                          heading: "Interview Tips",
                          content:
                            "Ask for code samples, portfolio links, and walk through problem solving.",
                        },
                      ]}
                    />
                  }
                />
                <Route
                  path="/talent-marketplace"
                  element={
                    <StaticInfoPage
                      title="Talent Marketplace"
                      description="Browse categories and find specialized freelancers."
                      sections={[
                        {
                          heading: "Browse by Category",
                          content: "Development, Design, Marketing, and more.",
                        },
                        {
                          heading: "Skill Tags",
                          content: "Use tags to quickly filter talent.",
                        },
                      ]}
                    />
                  }
                />
                <Route
                  path="/project-catalog"
                  element={
                    <StaticInfoPage
                      title="Project Catalog"
                      description="Fixed-price projects you can start instantly."
                      sections={[
                        {
                          heading: "Curated Packages",
                          content:
                            "Common deliverables at known prices and timelines.",
                        },
                        {
                          heading: "Customization",
                          content:
                            "Message sellers to adjust scope before purchasing.",
                        },
                      ]}
                    />
                  }
                />
                <Route path="/enterprise" element={<UpdatedEnterprisePage />} />
                <Route
                  path="/payroll-services"
                  element={
                    <StaticInfoPage
                      title="Payroll Services"
                      description="Compliance, payroll and taxes handled for you."
                      sections={[
                        {
                          heading: "Global Compliance",
                          content:
                            "Contracts, taxes, and local regulations covered.",
                        },
                        {
                          heading: "Consolidated Invoicing",
                          content: "One invoice for all your contractors.",
                        },
                      ]}
                    />
                  }
                />

                <Route
                  path="/how-to-find-work"
                  element={
                    <StaticInfoPage
                      title="How to Find Work"
                      description="Tips to stand out and win projects."
                      sections={[
                        {
                          heading: "Strong Profile",
                          content:
                            "Clear title, focused overview, and portfolio.",
                        },
                        {
                          heading: "Tailored Proposals",
                          content:
                            "Reference the client's goals and show relevant samples.",
                        },
                      ]}
                    />
                  }
                />
                <Route
                  path="/direct-contracts"
                  element={
                    <StaticInfoPage
                      title="Direct Contracts"
                      description="Work with clients directly with platform protections."
                      sections={[
                        {
                          heading: "Escrow",
                          content: "Funds are held until work is approved.",
                        },
                        {
                          heading: "Low Fees",
                          content: "Competitive fees for direct engagement.",
                        },
                      ]}
                    />
                  }
                />
                <Route
                  path="/find-freelance-jobs"
                  element={
                    <StaticInfoPage
                      title="Find Freelance Jobs"
                      description="Discover projects tailored to your skills."
                      sections={[
                        {
                          heading: "Smart Matching",
                          content: "We recommend jobs based on your profile.",
                        },
                        {
                          heading: "Search Filters",
                          content:
                            "Use hourly/fixed, budget, and location filters.",
                        },
                      ]}
                    />
                  }
                />
                <Route
                  path="/success-stories"
                  element={
                    <StaticInfoPage
                      title="Success Stories"
                      description="See how others thrive on CareerBuddyPro."
                      sections={[
                        {
                          heading: "Case Studies",
                          content:
                            "Real stories from companies and freelancers.",
                        },
                      ]}
                    />
                  }
                />
                <Route
                  path="/resources"
                  element={
                    <StaticInfoPage
                      title="Resources"
                      description="Guides, templates, and tools."
                      sections={[
                        {
                          heading: "Templates",
                          content: "Job description and interview templates.",
                        },
                        {
                          heading: "Guides",
                          content:
                            "Hiring, onboarding, and collaboration guides.",
                        },
                      ]}
                    />
                  }
                />

                <Route path="/about" element={<AboutPage />} />
                <Route
                  path="/leadership"
                  element={
                    <StaticInfoPage
                      title="Leadership"
                      description="Meet the team behind CareerBuddyPro."
                      sections={[
                        {
                          heading: "Leadership",
                          content:
                            "Experienced operators from top tech companies.",
                        },
                      ]}
                    />
                  }
                />
                <Route
                  path="/investors"
                  element={
                    <StaticInfoPage
                      title="Investor Relations"
                      description="Financials and governance."
                      sections={[
                        {
                          heading: "Reports",
                          content: "Quarterly updates and governance.",
                        },
                      ]}
                    />
                  }
                />
                <Route path="/careers" element={<CareersPage />} />

                {/* New Company & Business Pages */}
                <Route path="/for-companies" element={<ForCompaniesPage />} />
                <Route
                  path="/sign-contracts"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <SignContractsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/manage-team-projects"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <ManageTeamProjectsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/share-hiring-workspace"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <ShareHiringWorkspacePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/approve-invoices"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <ApproveInvoicesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/for-freelancers"
                  element={<ForFreelancersPage />}
                />
                <Route path="/join-as-talent" element={<JoinAsTalentPage />} />
                <Route
                  path="/manage-embedded"
                  element={<ManagedEmbeddedPage />}
                />
                <Route path="/for-agencies" element={<ForAgenciesPage />} />
                <Route path="/for-startups" element={<ForStartupsPage />} />

                <Route
                  path="/cookies"
                  element={
                    <StaticInfoPage
                      title="Cookie Policy"
                      description="Cookies we use and your choices."
                      sections={[
                        {
                          heading: "Essential Cookies",
                          content: "Required for site functionality.",
                        },
                        {
                          heading: "Analytics",
                          content: "Helps us improve experience.",
                        },
                      ]}
                    />
                  }
                />
                <Route
                  path="/accessibility"
                  element={
                    <StaticInfoPage
                      title="Accessibility"
                      description="Our commitment to inclusive access."
                      sections={[
                        {
                          heading: "Standards",
                          content: "We aim for WCAG 2.1 AA compliance.",
                        },
                      ]}
                    />
                  }
                />

                {/* New Features */}
                <Route path="/ai/job-matching" element={<AIJobMatching />} />
                <Route
                  path="/ai/proposal-assistant/:jobId"
                  element={<AIProposalAssistant />}
                />
                <Route path="/ai-features" element={<AIFeaturesPage />} />
                <Route
                  path="/team-workspaces"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <EnhancedTeamWorkspacesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/subscriptions"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <EnhancedSubscriptionsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/subscription/success"
                  element={<SubscriptionsPage />}
                />
                <Route
                  path="/subscription/cancel"
                  element={<SubscriptionsPage />}
                />
                <Route
                  path="/video-interviews"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <EnhancedVideoInterviewsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/video-room/:roomName"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <EnhancedVideoRoomPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/skills-marketplace"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <EnhancedSkillsMarketplacePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/gigs"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <EnhancedSkillsMarketplacePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/gigs/:gigId"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <EnhancedGigDetailsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/gigs/create"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                      <CreateGigPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/how-it-works-freelancers"
                  element={<HowItWorksFreelancersPage />}
                />

                {/* Legal & Info Pages */}
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsOfServicePage />} />
                <Route
                  path="/terms-of-service"
                  element={<TermsOfServicePage />}
                />
                <Route path="/contact" element={<ContactUsPage />} />
                <Route path="/contact-us" element={<ContactUsPage />} />

                {/* 404 Catch-all - Must be last */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </main>
          <EnhancedFooter />

          <ScrollToTop />
        </>
      ) : location.pathname.startsWith("/admin") && userType === "admin" ? (
        <main id="main-content">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/admin">
                <Route
                  path=""
                  element={
                    <AdminRoute>
                      <AdminDashboard />
                    </AdminRoute>
                  }
                ></Route>
                <Route
                  path="cms"
                  element={
                    <AdminRoute>
                      <CMSLayout />
                    </AdminRoute>
                  }
                >
                  <Route index element={<CMSDashboard />} />
                  <Route path="posts" element={<CMSPostsList />} />
                  <Route path="posts/new" element={<CMSPostEditor />} />
                  <Route
                    path="posts/:postId/edit"
                    element={<CMSPostEditor />}
                  />
                  <Route path="tags" element={<CMSTagsPage />} />
                  <Route path="authors" element={<CMSAuthorsPage />} />
                  <Route path="subscribers" element={<CMSSubscribersPage />} />
                  <Route path="settings" element={<CMSSettingsPage />} />
                </Route>
              </Route>
              <Route
                path="/admin/ops"
                element={
                  <AdminRoute>
                    <AdminOpsShell />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/fees"
                element={
                  <AdminRoute>
                    <FeeManagementPage />
                  </AdminRoute>
                }
              />
            </Routes>
          </Suspense>
        </main>
      ) : (
        <>
          <Header
            isLoggedIn={isLoggedIn}
            userType={userType}
            user={user}
            onLogout={onLogout}
          />

          <div className="text-center">
            <h1 className="font-semibold text-2xl my-20">Unathorized access</h1>
          </div>

          <EnhancedFooter />
        </>
      )}
    </>
  );
};

/**
 * PlatformSocketBridge
 *
 * Lives *inside* the provider tree so it can hand live WebSocket
 * messages off to the PresenceProvider (for presence/typing events)
 * and the NotificationProvider (for notifications), and still push
 * legacy toast messages for chat events. It also registers the socket
 * handle with the presence context so downstream consumers can send
 * typing pings.
 */
const PlatformSocketBridge = ({ token }) => {
  const wsRef = useRef(null);
  const { addToast } = useToast();
  const { setSocket, handleSocketMessage } = usePresence();
  const { pushNotification } = useNotifications();
  const { handleIncomingMessage } = useMessagesPanel();

  const { getWebSocket, lastMessage } = useWebSocket(
    token
      ? `${getSocketUrl(
          process.env.REACT_APP_BACKEND_URL || "",
        )}/api/v1/ws/chat?token=${token}`
      : null,
    {
      onOpen: () => {
        const ws = getWebSocket();
        wsRef.current = ws;
        setSocket(ws);
      },
      onClose: () => {
        wsRef.current = null;
        setSocket(null);
      },
    },
  );

  useEffect(() => {
    if (lastMessage == null) return;
    // Let the presence layer parse presence/typing events first — it
    // ignores anything it doesn't recognize, so this is always safe.
    handleSocketMessage(lastMessage.data);

    // Everything else (messages, invites, contract updates, ...) gets
    // routed to the notification center. We parse tolerantly because
    // the backend may send either a typed event or a raw chat payload.
    let msg = null;
    try {
      msg = JSON.parse(lastMessage.data);
    } catch {
      msg = null;
    }
    if (msg && typeof msg === "object") {
      switch (msg.type) {
        case "presence":
        case "presence_bulk":
        case "typing":
        case "typing_stop":
          // Already handled by presence layer above.
          break;
        case "notification":
          pushNotification({
            id: msg.id,
            category: msg.category,
            title: msg.title,
            body: msg.body,
            href: msg.href,
            created_at: msg.created_at,
            actor: msg.actor,
          });
          break;
        default: {
          // Treat as a new message if it looks like one.
          if (msg.conversation_id || msg.message) {
            handleIncomingMessage(msg);
            pushNotification({
              id:
                msg.id ||
                `msg-${msg.conversation_id || ""}-${msg.timestamp || Date.now()}`,
              category: "message",
              title: "New message",
              body: msg.message || msg.content || "",
              href: "/messages",
              created_at: msg.timestamp || new Date().toISOString(),
            });
            addToast({
              type: "success",
              message: "You have a new message. Go to conversations",
              duration: 10000,
            });
          }
          break;
        }
      }
    }
  }, [
    lastMessage,
    handleSocketMessage,
    pushNotification,
    addToast,
    handleIncomingMessage,
  ]);

  useEffect(() => {
    return () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
      setSocket(null);
    };
  }, [setSocket]);

  return null;
};

function App() {
  const initialAuth = getInitialAuthState();
  const [isLoggedIn, setIsLoggedIn] = useState(initialAuth.isLoggedIn);
  const [userType, setUserType] = useState(initialAuth.userType);
  const [user, setUser] = useState(initialAuth.user);
  const token = getToken();

  // Update auth state if storage changes (e.g., login/logout in another tab)
  useEffect(() => {
    const checkAuth = () => {
      try {
        const storedToken = getToken();
        if (storedToken) {
          const parsedUser = JSON.parse(localStorage.getItem("user"));
          setUser(parsedUser);
          setUserType(localStorage.getItem("userType"));
          setIsLoggedIn(true);
        } else {
          setUser(null);
          setUserType("");
          setIsLoggedIn(false);
        }
      } catch (e) {
        setUser(null);
        setUserType("freelancer");
        setIsLoggedIn(false);
      }
    };

    // Check on mount and listen for storage changes
    checkAuth();
    window.addEventListener("storage", checkAuth);
    return () => window.removeEventListener("storage", checkAuth);
  }, []);

  const handleLogout = useCallback(() => {
    // Delegated to the shared helper so the refresh token is cleared too.
    clearAuth();
    setUser(null);
    setUserType("freelancer");
    setIsLoggedIn(false);
    window.location = "/login";
  }, []);

  // Gate the entire app behind a maintenance screen when the env var is set.
  if (process.env.REACT_APP_MAINTENANCE_MODE === "true") {
    return <MaintenancePage />;
  }

  return (
    <div className="App">
      <ToastProvider>
        <LocaleProvider user={user}>
          <PresenceProvider>
            <NotificationProvider isLoggedIn={isLoggedIn}>
              <MessagesPanelProvider>
                <BrowserRouter>
                  <CommandPaletteProvider
                    isLoggedIn={isLoggedIn}
                    userType={userType}
                  >
                    <SkipToContent />
                    <PlatformSocketBridge token={token} />
                    <MessagesPanel />
                    <Root
                      user={user}
                      isLoggedIn={isLoggedIn}
                      userType={userType}
                      onLogout={handleLogout}
                    />
                  </CommandPaletteProvider>
                </BrowserRouter>
              </MessagesPanelProvider>
            </NotificationProvider>
          </PresenceProvider>
        </LocaleProvider>
      </ToastProvider>
    </div>
  );
}

export default App;
